/**
 * Analytics Reporting Service
 * Provides access to real-time analytics data via the backend API.
 * Authenticated admin requests include a Firebase ID token.
 */

import { auth } from '../firebase';
import { resolveApiBaseUrl } from '../utils/apiBaseUrl';

class AnalyticsReportingService {
  /**
   * Build request headers with the current user's Firebase ID token.
   * @returns {Promise<HeadersInit>}
   */
  static async getAuthHeaders() {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('You must be signed in to view analytics');
    }

    const token = await user.getIdToken();
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Perform an authenticated GET request against the analytics API.
   * @param {string} path
   * @returns {Promise<Object>}
   */
  static async authenticatedGet(path) {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${resolveApiBaseUrl()}${path}`, { headers });

    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        message = errorData.error || message;
      } catch (parseError) {
        // Keep default message when the body is not JSON
      }
      throw new Error(message);
    }

    return response.json();
  }

  /**
   * Append optional date range query params to a URL path.
   * @param {string} path
   * @param {{ startDate?: string, endDate?: string }} options
   * @returns {string}
   */
  static withDateRange(path, options = {}) {
    const params = new URLSearchParams();
    if (options.startDate) params.set('startDate', options.startDate);
    if (options.endDate) params.set('endDate', options.endDate);
    const query = params.toString();
    return query ? `${path}?${query}` : path;
  }

  /**
   * Get analytics summary for admin dashboard
   * @param {string} organizationId - Organization to get data for
   * @param {string} period - 'today', 'week', 'month', 'lifetime'
   * @returns {Promise<Object>} Summary metrics for the specified period
   */
  static async getAnalyticsSummary(organizationId, period = 'lifetime') {
    try {
      const data = await this.authenticatedGet(
        `/api/analytics/summary/${organizationId}?period=${period}`
      );
      return data.summary;
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      throw error;
    }
  }

  /**
   * Get detailed analytics data for a specific date range
   * @param {string} organizationId - Organization to get data for
   * @param {string} startDate - Start date (YYYY-MM-DD format)
   * @param {string} endDate - End date (YYYY-MM-DD format)
   * @param {string|null} eventType - Optional event type filter
   * @returns {Promise<Array>} Array of events
   */
  static async getDetailedAnalytics(organizationId, startDate, endDate, eventType = null) {
    try {
      let path = `/api/analytics/detailed/${organizationId}?startDate=${startDate}&endDate=${endDate}`;
      if (eventType) {
        path += `&eventType=${eventType}`;
      }

      const data = await this.authenticatedGet(path);
      return data.events;
    } catch (error) {
      console.error('Error fetching detailed analytics:', error);
      throw error;
    }
  }

  /**
   * Get per-user activity summary for an organization
   * @param {string} organizationId - Organization to get data for
   * @param {{ startDate?: string, endDate?: string }} options - Optional date range
   * @returns {Promise<Array>} Array of user activity data
   */
  static async getUserActivitySummary(organizationId, options = {}) {
    try {
      const path = this.withDateRange(`/api/analytics/users/${organizationId}`, options);
      const data = await this.authenticatedGet(path);
      return data.userStats || [];
    } catch (error) {
      console.error('Error fetching user activity summary:', error);
      throw error;
    }
  }

  /**
   * Get detailed statistics for a single user
   * @param {string} organizationId - Organization context
   * @param {string} userId - Target user ID
   * @param {{ startDate?: string, endDate?: string }} options - Optional date range
   * @returns {Promise<Object>} Detailed user statistics
   */
  static async getUserStatsDetail(organizationId, userId, options = {}) {
    try {
      const path = this.withDateRange(
        `/api/analytics/users/${organizationId}/${userId}`,
        options
      );
      return await this.authenticatedGet(path);
    } catch (error) {
      console.error('Error fetching user statistics detail:', error);
      throw error;
    }
  }

  /**
   * Get prompts-specific analytics data
   * @param {string} organizationId - Organization to get data for
   * @param {string} period - 'today', 'week', 'month', 'lifetime'
   * @returns {Promise<Object>} Prompts analytics data
   */
  static async getPromptsAnalytics(organizationId, period = 'lifetime') {
    try {
      const summary = await this.getAnalyticsSummary(organizationId, period);

      return {
        promptsAccessed: summary.promptsAccessed || 0,
        promptsCompleted: summary.promptsCompleted || 0,
        writingPromptsAccessed: summary.promptsAccessed || 0,
        writingPromptsCompleted: summary.promptsCompleted || 0
      };
    } catch (error) {
      console.error('Error fetching prompts analytics:', error);
      throw error;
    }
  }

  /**
   * Format unique/total resource counts for compact table display.
   * @param {{ accessedUnique?: number, accessedTotal?: number }} metrics
   * @returns {string}
   */
  static formatAccessCounts(metrics) {
    if (!metrics) return '—';
    const unique = metrics.accessedUnique ?? 0;
    const total = metrics.accessedTotal ?? 0;
    return `${unique} unique / ${total} visits`;
  }

  /**
   * Get analytics data for multiple periods for comparison
   * @param {string} organizationId - Organization to get data for
   * @param {Array<string>} periods - Array of periods to compare
   * @returns {Promise<Object>} Comparison data
   */
  static async getPeriodComparison(organizationId, periods = ['today', 'week', 'month']) {
    try {
      const comparisonData = {};

      for (const period of periods) {
        try {
          const summary = await this.getAnalyticsSummary(organizationId, period);
          comparisonData[period] = summary;
        } catch (error) {
          console.warn(`Failed to get data for period ${period}:`, error);
          comparisonData[period] = null;
        }
      }

      return comparisonData;
    } catch (error) {
      console.error('Error getting period comparison:', error);
      throw error;
    }
  }

  /**
   * Format analytics data for charts
   * @param {Object} summary - Analytics summary data
   * @returns {Object|null} Formatted data for charting libraries
   */
  static formatForCharts(summary) {
    if (!summary) return null;

    return {
      metrics: {
        labels: ['Courses', 'Lessons', 'Tasks', 'Prompts', 'Logins'],
        datasets: [{
          label: 'Accessed',
          data: [
            summary.coursesAccessed || 0,
            summary.lessonsAccessed || 0,
            summary.tasksCompleted || 0,
            summary.promptsAccessed || 0,
            summary.logins || 0
          ],
          backgroundColor: [
            'rgba(54, 162, 235, 0.8)',
            'rgba(75, 192, 192, 0.8)',
            'rgba(255, 206, 86, 0.8)',
            'rgba(153, 102, 255, 0.8)',
            'rgba(255, 99, 132, 0.8)'
          ]
        }]
      },
      completion: {
        labels: ['Lessons', 'Courses', 'Prompts'],
        datasets: [{
          label: 'Completed',
          data: [
            summary.lessonsCompleted || 0,
            summary.coursesCompleted || 0,
            summary.promptsCompleted || 0
          ],
          backgroundColor: [
            'rgba(75, 192, 192, 0.8)',
            'rgba(54, 162, 235, 0.8)',
            'rgba(153, 102, 255, 0.8)'
          ]
        }]
      },
      users: {
        labels: ['Total Users', 'New Users', 'Active Users'],
        datasets: [{
          label: 'User Counts',
          data: [
            summary.totalUsers || 0,
            summary.newUsers || 0,
            summary.uniqueUsers || 0
          ],
          backgroundColor: [
            'rgba(255, 99, 132, 0.8)',
            'rgba(75, 192, 192, 0.8)',
            'rgba(54, 162, 235, 0.8)'
          ]
        }]
      }
    };
  }

  /**
   * Calculate growth rates between periods
   * @param {Object} currentPeriod - Current period data
   * @param {Object} previousPeriod - Previous period data
   * @returns {Object|null} Growth rate calculations
   */
  static calculateGrowthRates(currentPeriod, previousPeriod) {
    if (!currentPeriod || !previousPeriod) {
      return null;
    }

    const calculateGrowth = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    return {
      coursesAccessed: calculateGrowth(currentPeriod.coursesAccessed || 0, previousPeriod.coursesAccessed || 0),
      lessonsAccessed: calculateGrowth(currentPeriod.lessonsAccessed || 0, previousPeriod.lessonsAccessed || 0),
      tasksCompleted: calculateGrowth(currentPeriod.tasksCompleted || 0, previousPeriod.tasksCompleted || 0),
      lessonsCompleted: calculateGrowth(currentPeriod.lessonsCompleted || 0, previousPeriod.lessonsCompleted || 0),
      coursesCompleted: calculateGrowth(currentPeriod.coursesCompleted || 0, previousPeriod.coursesCompleted || 0),
      uniqueUsers: calculateGrowth(currentPeriod.uniqueUsers || 0, previousPeriod.uniqueUsers || 0),
      newUsers: calculateGrowth(currentPeriod.newUsers || 0, previousPeriod.newUsers || 0)
    };
  }
}

export default AnalyticsReportingService;
